import {
  useMemo,
  useState,
  useRef,
  useEffect,
  ReactNode,
  forwardRef
} from "react";
import { type SortingState } from "@tanstack/react-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cx } from "shared/utils";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import {
  Action,
  ContextMenuPopover
} from "shared/components/ContextMenuPopover";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CircleIcon,
  Delete01Icon,
  CheckmarkCircle02Icon as HoverIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { CheckmarkCircle02Icon as DoneIcon } from "@hugeicons-pro/core-solid-rounded";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { api } from "api/task";
import { useAuthContext } from "providers/Auth";
import { Column, SELECT_COLUMN_KEY, SortColumn } from "react-data-grid";
import { DataGridTable } from "shared/components/DataGridTable";
import { Avatar } from "shared/components/Avatar";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  Show
} from "shared/components";
import { User } from "views/settings-users/types";
import { useLayout } from "providers/Layout/hooks";
import { useMedia } from "shared/providers";
import { HeaderWithSort } from "./HeaderWithSort";
import { getErrorMessage } from "utils/error";
import { TaskStatus, Task } from "../../types";
import {
  columnName,
  DUE_COLOR,
  dueLabel,
  formatTaskCreatedDate,
  RelatedMapping,
  TaskOptions,
  toRdgSort,
  toTanStackSort
} from "../../constants";
import moment from "moment";

type SectionHeaderRow = {
  id: string;
  isSectionHeader: true;
  label: string;
};

type TaskRow = (Task & { selected?: boolean }) | SectionHeaderRow;

const isSectionHeader = (row: TaskRow | undefined): row is SectionHeaderRow =>
  row != null && "isSectionHeader" in row;

type Props = {
  data: Task[];
  sorting: SortingState;
  onSortingChange: (next: SortingState) => void;
  isLoading?: boolean;
  pagination?: ReactNode;
  perPageRows?: number;
  onDeleted?: () => void;
  onTaskView?: (task: Task) => void;
  loadingTaskId?: number | null;
  showCompleted?: boolean;
  noRowsFallback?: ReactNode;
  hideRelatedColumn?: boolean;
};
const buildDueLabel = (iso?: string | null): string | null => {
  if (!iso) return null;
  const due = moment.utc(iso).startOf("day");
  const diff = due.diff(moment.utc().startOf("day"), "days");
  if (diff < 0) {
    const n = Math.abs(diff);
    return `Due ${n} day${n === 1 ? "" : "s"} ago`;
  }
  if (dueLabel[diff]) return dueLabel[diff];
  return `Due ${due.format("MMM D, YYYY")}`;
};

const TitleContent = forwardRef<
  HTMLDivElement,
  {
    title: string;
    isDone: boolean;
    textRef: React.RefObject<HTMLParagraphElement>;
  }
>(({ title, isDone, textRef }, ref) => (
  <div className="w-full max-w-[540px]" ref={ref}>
    <div
      className={cx(
        "block w-full text-left cursor-pointer text-content-1000",
        isDone ? "opacity-60" : ""
      )}
    >
      <p
        ref={textRef}
        dangerouslySetInnerHTML={{ __html: title }}
        className="w-full text-sm font-medium truncate max-h-[20px]"
      />
    </div>
  </div>
));

TitleContent.displayName = "TitleContent";

const TitleCell = ({
  title,
  titleHtml,
  isDone
}: {
  title: string;
  titleHtml: string;
  isDone: boolean;
}) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current)
        setIsTruncated(
          textRef.current.scrollWidth > textRef.current.clientWidth
        );
    };

    const timeoutId = setTimeout(checkTruncation, 0);
    return () => clearTimeout(timeoutId);
  }, [titleHtml]);

  const content = (
    <TitleContent title={title} isDone={isDone} textRef={textRef} />
  );

  return isTruncated ? (
    <Tooltip placement="bottom">
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent className="rounded-lg p-4 max-w-[379px] w-full bg-alpha-800 border">
        <span className="text-sm text-white">{title}</span>
      </TooltipContent>
    </Tooltip>
  ) : (
    content
  );
};

const AssigneeCell = ({
  assignees,
  status
}: {
  assignees: User[] | null;
  status: TaskStatus | undefined;
}) => {
  const { auth } = useAuthContext();
  const isComplete = status === TaskStatus.COMPLETED;
  if (!assignees?.length)
    return (
      <span
        className={cx(
          "px-2 py-1 text-content-400",
          isComplete ? "opacity-60" : ""
        )}
      />
    );
  const a = assignees[0];
  const remainder = assignees.length - 1;

  return (
    <div className="border-alpha-40 flex justify-start gap-2 cursor-pointer">
      <Badge
        className="shrink-0 items-center leading-none gap-2"
        size={BadgeSize.LG}
        variant={BadgeVariant.BASE}
        leftElement={
          <>
            <Avatar
              name={a.name}
              className="text-[9px] text-content-1000"
              size={20}
            />
            <span
              className={cx(
                "text-xs text-content-1000 font-medium",
                isComplete ? "opacity-60" : ""
              )}
            >
              {a.name}
            </span>
          </>
        }
        rightElement={
          <Show if={auth!.name === a.name}>
            <span
              className={cx(
                "font-medium text-content-200 text-xs",
                isComplete ? "opacity-60" : ""
              )}
            >
              (You)
            </span>
          </Show>
        }
      />

      <Show if={assignees.length > 1}>
        <Tooltip placement="bottom">
          <TooltipTrigger asChild>
            <div className="flex items-center">
              <Badge
                className="shrink-0"
                size={BadgeSize.BASE}
                variant={BadgeVariant.BASE}
              >
                +{remainder}
              </Badge>
            </div>
          </TooltipTrigger>
          <TooltipContent className="max-w-[50vw] rounded-lg p-4 bg-alpha-800 shadow-xl">
            <div className="flex gap-2 flex-wrap bg-transparent">
              {assignees.slice(1).map((assignee) => (
                <div
                  key={assignee.id}
                  className="px-2 py-1 flex items-center gap-2 justify-center"
                >
                  <Avatar
                    name={assignee.name}
                    className="text-[9px] font-black"
                    size={20}
                  />
                  <span className="text-xs text-white font-medium">
                    {assignee.name}
                  </span>
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      </Show>
    </div>
  );
};

const getDueClassName = (value: string | null, isDone?: boolean): string => {
  const doneClassName = isDone ? "opacity-60" : "";
  if (!value)
    return cx("font-medium text-content-400 cursor-pointer", doneClassName);

  const v = value.toLowerCase();
  const dueColor =
    Object.entries(DUE_COLOR).find(([_, patterns]) =>
      patterns.some((p) => v.includes(p))
    )?.[0] ?? "text-content-400";

  return cx("font-medium cursor-pointer", dueColor, doneClassName);
};

const DueCell = ({
  value,
  isDone
}: {
  value: string | null;
  isDone?: boolean;
}) => <span className={getDueClassName(value, isDone)}>{value || "—"}</span>;

const StatusCell = ({
  row,
  onMarkComplete,
  onReopen
}: {
  row: Task;
  onMarkComplete: (r: Task) => void;
  onReopen: (r: Task) => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  const isDone = row.status === TaskStatus.COMPLETED;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDone) onReopen(row);
    else {
      setJustCompleted(true);
      setTimeout(() => {
        setJustCompleted(false);
        onMarkComplete(row);
      }, 2000);
    }
  };

  const showDone = isDone || justCompleted;
  const getIcon = () => {
    if (showDone) return DoneIcon;
    if (isHovered) return HoverIcon;
    return CircleIcon;
  };
  const iconClass = showDone ? "text-success-200" : "text-content-300";

  return (
    <div className="h-full w-full grid place-items-center">
      <button
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <HugeiconsIcon
          icon={getIcon()}
          className={cx(iconClass)}
          size={20}
          stroke="1.2px"
        />
      </button>
    </div>
  );
};

const RelatedCell = ({ row }: { row: Task }) => {
  const isDone = row.status === TaskStatus.COMPLETED;

  if (!row.subject)
    return (
      <div
        className={cx("space-y-2 cursor-pointer", isDone ? "opacity-60" : "")}
      >
        <span className="font-medium text-xs text-content-400">—</span>
      </div>
    );

  return (
    <div className={cx("space-y-2 cursor-pointer", isDone ? "opacity-60" : "")}>
      <span className="font-medium text-xs">
        {RelatedMapping[row.subject.type]}
      </span>
      <span className="block text-xs text-content-400 truncate">
        {row.subject.name}
      </span>
    </div>
  );
};
const CreatedAtCell = ({ row }: { row: Task }) => {
  const { label, time } = formatTaskCreatedDate(row.created_at);
  return (
    <div
      className={cx(
        "space-y-2 cursor-pointer",
        row.status === TaskStatus.COMPLETED ? "opacity-60" : ""
      )}
    >
      <span className="text-sm text-content-1000">{label}</span>

      <span className="text-xs text-content-400 block">{time}</span>
    </div>
  );
};

const ActionsCell = ({
  row,
  onDelete
}: {
  row: Task;
  onDelete: (r: Task) => void;
}) => {
  const popoverActions: Action[] = [
    {
      title: (
        <div className="flex gap-3 items-center">
          <HugeiconsIcon icon={Delete01Icon} size={20} stroke="1.5px" />
          <span className="font-medium text-base text-content-1000">
            {TaskOptions.delete}
          </span>
        </div>
      ),
      onClick: (e) => {
        e.stopPropagation();
        onDelete(row);
      },
      className: "py-2.5 px-4"
    }
  ];

  return (
    <div className="flex justify-end">
      <ContextMenuPopover
        placement="bottom-start"
        triggerClassName="-ml-1.5"
        actions={popoverActions}
        contentClassName="p-0 !max-w-[192px] w-full"
      />
    </div>
  );
};

const transformDataWithSections = (
  tasks: Task[],
  showCompleted: boolean
): TaskRow[] => {
  const incompleteTasks = tasks.filter(
    (t) => t.status === TaskStatus.INCOMPLETED
  );
  const completedTasks = tasks.filter((t) => t.status === TaskStatus.COMPLETED);

  const rows: TaskRow[] = [];

  if (incompleteTasks.length > 0) {
    rows.push({
      id: "section-incomplete",
      isSectionHeader: true,
      label: "Incomplete"
    } as SectionHeaderRow);
    rows.push(...incompleteTasks);
  }

  if (showCompleted && completedTasks.length > 0) {
    rows.push({
      id: "section-completed",
      isSectionHeader: true,
      label: "Completed"
    } as SectionHeaderRow);
    rows.push(...completedTasks);
  }

  return rows;
};

export const TasksTable = ({
  data,
  sorting,
  onSortingChange,
  isLoading,
  pagination,
  perPageRows,
  onDeleted,
  onTaskView,
  loadingTaskId,
  showCompleted = false,
  noRowsFallback,
  hideRelatedColumn = false
}: Props) => {
  const { sectionHeight } = useLayout();
  const { isMobile } = useMedia();
  const queryClient = useQueryClient();
  const viewPortHeightOffset = isMobile ? 200 : 115;
  const transformedData = useMemo(
    () => transformDataWithSections(data, showCompleted),
    [data, showCompleted]
  );
  const { mutate: deleteTask } = useMutation({
    mutationKey: ["DELETE_TASK"],
    mutationFn: (taskId: number) => api.deleteTask(taskId),
    onSuccess: (_, taskId) => {
      queryClient.refetchQueries({ queryKey: ["TASKS_TABLE"] });
      onDeleted?.();
      const onUndo = async () => {
        await api.undoDeleteTask(taskId);
        queryClient.refetchQueries({ queryKey: ["TASKS_TABLE"] });
        onDeleted?.();
      };
      toast.success(
        ({ closeToast }) => (
          <div className="flex items-center gap-4 justify-between">
            <div className="flex items-center gap-2">
              <HugeiconsIcon
                icon={HoverIcon}
                className="text-success-600"
                size={20}
              />
              <span className="text-base font-bold text-content-1000">
                Task deleted
              </span>
            </div>
            <Button
              onClick={() => {
                onUndo();
                closeToast();
              }}
              size={ButtonSize.SM}
            >
              Undo
            </Button>
          </div>
        ),
        {
          autoClose: 30000,
          hideProgressBar: false,
          progressClassName: "!bg-success-600",
          className:
            "!bg-background-1000 border border-alpha-100 shadow-lg rounded-lg max-w-[272px] w-full",
          bodyClassName: "p-4",
          icon: false
        }
      );
    },
    onError: (e) => {
      const message = getErrorMessage(e);
      toast.error(message);
    }
  });

  const { mutate: completeTask } = useMutation({
    mutationKey: ["COMPLETE_TASK"],
    mutationFn: (taskId: number) => api.completeTask(taskId),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["TASKS_TABLE"] });
      onDeleted?.();
      toast.success("Task completed");
    },
    onError: (e) => {
      const message = getErrorMessage(e);
      toast.error(message);
    }
  });

  const { mutate: reopenTask } = useMutation({
    mutationKey: ["REOPEN_TASK"],
    mutationFn: (taskId: number) => api.reopenTask(taskId),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["TASKS_TABLE"] });
      onDeleted?.();
      toast.success("Task reopened");
    },
    onError: (e) => {
      if (
        e instanceof AxiosError &&
        e.response?.data?.error?.message === "cannot_reopen_after_24h"
      ) {
        toast.error("Cannot reopen task after 24 hours of completion");
        return;
      }
      const message = getErrorMessage(e);
      toast.error(message);
    }
  });

  const handleMarkCompleteTask = (row: Task) => {
    const taskId = Number(row.id);
    completeTask(taskId);
  };

  const handleReopenTask = (row: Task) => {
    const taskId = Number(row.id);
    reopenTask(taskId);
  };
  const handleSort = (columnKey: string) => {
    const existingSort = sorting.find((s) => s.id === columnKey);

    let newSorting: SortingState;

    if (existingSort)
      newSorting = sorting.map((s) =>
        s.id === columnKey ? { ...s, desc: !s.desc } : s
      );
    else {
      const isDueDate = columnKey === "dueLabel";
      newSorting = [{ id: columnKey, desc: !isDueDate }];
    }

    onSortingChange(newSorting);
  };

  const getSortDirection = (columnKey: string) => {
    const sortItem = sorting.find((s) => s.id === columnKey);
    if (!sortItem) return undefined;
    return sortItem.desc ? "DESC" : "ASC";
  };

  const columns = useMemo<Column<TaskRow>[]>(
    () => [
      {
        key: SELECT_COLUMN_KEY,
        name: "",
        width: 55,
        minWidth: 52,
        resizable: false,
        headerCellClass: "border-r border-alpha-40 place-items-center grid",
        cellClass: (row) =>
          isSectionHeader(row)
            ? "border-0 !bg-alpha-20"
            : "border-r border-alpha-40",
        renderCell: ({ row }) => {
          if (isSectionHeader(row)) return null;

          return (
            <StatusCell
              row={row}
              onMarkComplete={handleMarkCompleteTask}
              onReopen={handleReopenTask}
            />
          );
        }
      },
      {
        key: "title",
        name: columnName.title,
        minWidth: 250,
        resizable: true,
        cellClass: (row) =>
          isSectionHeader(row) ? "border-0 !bg-alpha-20" : "",
        renderCell: ({ row }) => {
          if (isSectionHeader(row))
            return (
              <div className="h-full flex items-center">
                <span className="font-medium text-sm text-content-400">
                  {row.label}
                </span>
              </div>
            );

          return (
            <TitleCell
              title={row.title.document_text}
              titleHtml={row.title.document_html || ""}
              isDone={row.status === TaskStatus.COMPLETED}
            />
          );
        }
      },
      {
        key: "related",
        name: columnName.related,
        minWidth: 260,
        resizable: true,
        cellClass: (row) =>
          isSectionHeader(row) ? "border-0 !bg-alpha-20" : "",
        renderCell: ({ row }) => {
          if (isSectionHeader(row)) return null;
          return <RelatedCell row={row} />;
        }
      },
      {
        key: "dueLabel",
        name: "",
        minWidth: 160,
        resizable: true,
        cellClass: (row) =>
          isSectionHeader(row) ? "border-0 !bg-alpha-20" : "",
        renderHeaderCell: () => (
          <HeaderWithSort
            label={columnName.due_date}
            columnKey="dueLabel"
            sortDirection={getSortDirection("dueLabel")}
            onSort={handleSort}
          />
        ),
        renderCell: ({ row }) => {
          if (isSectionHeader(row)) return null;

          return (
            <DueCell
              value={buildDueLabel(row.due_date)}
              isDone={row.status === TaskStatus.COMPLETED}
            />
          );
        }
      },
      {
        key: "assignees",
        name: columnName.assignees,
        minWidth: 260,
        resizable: true,
        cellClass: (row) =>
          isSectionHeader(row) ? "border-0 !bg-alpha-20" : "",
        renderCell: ({ row }) => {
          if (isSectionHeader(row)) return null;
          return <AssigneeCell assignees={row.assignees} status={row.status} />;
        }
      },
      {
        key: "createdAt",
        name: "",
        minWidth: 140,
        resizable: true,
        cellClass: (row) =>
          isSectionHeader(row) ? "border-0 !bg-alpha-20" : "",
        renderHeaderCell: () => (
          <HeaderWithSort
            label={columnName.createdAt}
            columnKey="createdAt"
            sortDirection={getSortDirection("createdAt")}
            onSort={handleSort}
          />
        ),
        renderCell: ({ row }) => {
          if (isSectionHeader(row)) return null;
          return <CreatedAtCell row={row} />;
        }
      },
      {
        key: "action",
        name: "",
        minWidth: 100,
        resizable: true,
        cellClass: (row) =>
          isSectionHeader(row) ? "border-0 !bg-alpha-20" : "",
        renderCell: ({ row }) => {
          if (isSectionHeader(row)) return null;

          return (
            <ActionsCell row={row} onDelete={(r) => deleteTask(Number(r.id))} />
          );
        }
      }
    ],
    [transformedData, loadingTaskId]
  ).filter((col) => !(hideRelatedColumn && col.key === "related"));
  const sortColumns = useMemo<SortColumn[]>(
    () => toRdgSort(sorting),
    [sorting]
  );
  const showLoading = isLoading && transformedData.length === 0;
  const isEmpty = !isLoading && transformedData.length === 0;

  return (
    <DataGridTable
      columns={columns}
      rows={transformedData}
      loading={showLoading}
      rowHeight={showLoading ? 72 : (row) => (isSectionHeader(row) ? 48 : 72)}
      headerRowHeight={isEmpty ? 0 : 44}
      wrapperClassName="rounded-lg border border-alpha-100 w-full"
      className="boom-react-data-grid overscroll-none h-fit w-full"
      viewPortHeight={sectionHeight}
      viewPortHeightOffset={viewPortHeightOffset}
      perPageRows={perPageRows}
      sortColumns={sortColumns}
      onSortColumnsChange={(next) => {
        onSortingChange(toTanStackSort(next));
      }}
      noRowsFallback={noRowsFallback}
      footer={!isEmpty ? pagination : undefined}
      rowKeyGetter={
        showLoading
          ? undefined
          : (row) => (isSectionHeader(row) ? row.id : `task-${row.id}`)
      }
      rowClass={
        showLoading
          ? undefined
          : (row) => (isSectionHeader(row) ? "rdg-section-header" : "")
      }
      onRowClick={(row) => {
        if (!isSectionHeader(row)) onTaskView?.(row);
      }}
    />
  );
};
